import { useAuth0, User } from '@auth0/auth0-react'

import { useEffect, useState } from 'react'

import { isSignupFlowActive } from '../config/auth0'

const EMAIL_VERIFIED_CACHE_PREFIX = 'scrubstr_email_verified_'
const VERIFY_EMAIL_DISMISSED_PREFIX = 'scrubstr_verify_email_dismissed_'
export const EMAIL_VERIFIED_EVENT = 'scrubstr_email_verified'
export const SIGNUP_VERIFY_FLOW_UPDATED_EVENT = 'scrubstr_signup_verify_flow_updated'

const notifySignupVerifyFlowUpdated = (): void => {
  window.dispatchEvent(new CustomEvent(SIGNUP_VERIFY_FLOW_UPDATED_EVENT))
}

const isEmailVerifiedLocally = (userId: string): boolean => {
  try {
    return localStorage.getItem(`${EMAIL_VERIFIED_CACHE_PREFIX}${userId}`) === 'true'
  } catch {
    return false
  }
}

export const markEmailVerifiedLocally = (userId: string): void => {
  try {
    localStorage.setItem(`${EMAIL_VERIFIED_CACHE_PREFIX}${userId}`, 'true')
    sessionStorage.removeItem(`${VERIFY_EMAIL_DISMISSED_PREFIX}${userId}`)
  } catch {
    // ignore write failures (e.g. private browsing quota)
  }
  window.dispatchEvent(new CustomEvent(EMAIL_VERIFIED_EVENT))
  notifySignupVerifyFlowUpdated()
}

export const clearEmailVerifiedLocally = (userId: string): void => {
  try {
    localStorage.removeItem(`${EMAIL_VERIFIED_CACHE_PREFIX}${userId}`)
  } catch {
    // ignore
  }
}

export const markVerifyEmailDismissed = (userId: string): void => {
  try {
    sessionStorage.setItem(`${VERIFY_EMAIL_DISMISSED_PREFIX}${userId}`, 'true')
  } catch {
    // ignore
  }
  notifySignupVerifyFlowUpdated()
}

export const isVerifyEmailDismissed = (userId: string): boolean => {
  try {
    return sessionStorage.getItem(`${VERIFY_EMAIL_DISMISSED_PREFIX}${userId}`) === 'true'
  } catch {
    return false
  }
}

export const clearVerifyEmailDismissed = (userId: string): void => {
  try {
    sessionStorage.removeItem(`${VERIFY_EMAIL_DISMISSED_PREFIX}${userId}`)
  } catch {
    // ignore
  }
}

/** Clears all client-side email verification overrides for a user (call on logout). */
export const clearEmailVerificationState = (userId: string): void => {
  clearEmailVerifiedLocally(userId)
  clearVerifyEmailDismissed(userId)
}

export const canPerformVerifiedAction = (isAuthenticated: boolean, user?: User | null): boolean => {
  if (!isAuthenticated || !user?.sub) {
    return false
  }

  return !!user.email_verified || isEmailVerifiedLocally(user.sub)
}

/** Post-signup one-screen verify prompt: show immediately after Auth0 signup redirect. */
export const shouldShowSignupVerifyPrompt = (isAuthenticated: boolean, user?: User | null): boolean => {
  if (!isAuthenticated || !user?.sub || !isSignupFlowActive()) {
    return false
  }

  if (canPerformVerifiedAction(isAuthenticated, user)) {
    return false
  }

  return !isVerifyEmailDismissed(user.sub)
}

/** Suppress page-level write gates while the post-signup verify prompt is active. */
export const shouldDeferWriteGate = (isAuthenticated: boolean, user?: User | null): boolean => {
  return shouldShowSignupVerifyPrompt(isAuthenticated, user)
}

/** Re-renders when the user confirms verification via the modal (Auth0 JWT may still be stale). */
export const useCanPerformVerifiedAction = (): boolean => {
  const { user, isAuthenticated } = useAuth0()
  const [, setRevision] = useState(0)

  useEffect(() => {
    const onUpdate = () => setRevision((value) => value + 1)
    window.addEventListener(EMAIL_VERIFIED_EVENT, onUpdate)
    window.addEventListener(SIGNUP_VERIFY_FLOW_UPDATED_EVENT, onUpdate)
    return () => {
      window.removeEventListener(EMAIL_VERIFIED_EVENT, onUpdate)
      window.removeEventListener(SIGNUP_VERIFY_FLOW_UPDATED_EVENT, onUpdate)
    }
  }, [])

  return canPerformVerifiedAction(isAuthenticated, user)
}

export const useShouldDeferWriteGate = (): boolean => {
  const { user, isAuthenticated } = useAuth0()
  const [, setRevision] = useState(0)

  useEffect(() => {
    const onUpdate = () => setRevision((value) => value + 1)
    window.addEventListener(SIGNUP_VERIFY_FLOW_UPDATED_EVENT, onUpdate)
    window.addEventListener(EMAIL_VERIFIED_EVENT, onUpdate)
    return () => {
      window.removeEventListener(SIGNUP_VERIFY_FLOW_UPDATED_EVENT, onUpdate)
      window.removeEventListener(EMAIL_VERIFIED_EVENT, onUpdate)
    }
  }, [])

  return shouldDeferWriteGate(isAuthenticated, user)
}
